// สรุปรอบเข้าเยี่ยมร้าน — mirrors GET /dashboard/visit-frequency and
// GET /dashboard/visit-frequency/customers (Phase 1: visit data only, no plan/pathway).

/**
 * `frequency` when the range holds at least 6 working days; below that a shop can only be
 * told apart as เข้าร้าน / ไม่เข้าร้าน, so the server switches to `visited_only`.
 */
export type VisitFrequencyMode = "frequency" | "visited_only";

export type FrequencyBucketKey =
  | "daily"
  | "alternate"
  | "twice_weekly"
  | "weekly"
  | "none";

export type VisitedOnlyBucketKey = "visited" | "none";

export type VisitBucketKey = FrequencyBucketKey | VisitedOnlyBucketKey;

/** Only the keys of the response's mode are present. */
export type VisitBucketCounts = Partial<Record<VisitBucketKey, number>>;

/**
 * Lower bound (in visit days) of each bucket, as the server computed it for this range —
 * e.g. N=25 → `{ daily: 24, alternate: 11, twice_weekly: 6, weekly: 1 }`. The FE never
 * recomputes these; it only turns them into ranges for the headers.
 */
export type VisitThresholds = Partial<Record<VisitBucketKey, number>>;

export interface VisitFrequencyCounts {
  shops: number;
  suspended_shops: number;
  buckets: VisitBucketCounts;
  visited_not_sold_shops: number;
}

export interface VisitFrequencyRoute extends VisitFrequencyCounts {
  /** 4-digit key (`0301`); `null` = shops with no route ("ไม่มีสาย"), always listed last. */
  route_number: string | null;
  route_code: string | null;
  route_name: string | null;
}

export interface VisitFrequencyBranch extends VisitFrequencyCounts {
  company_id: string;
  branch_code: string;
  branch_name: string;
  route_count: number;
  /** Present only when the request named a `branch_code`. */
  routes?: VisitFrequencyRoute[];
}

export interface VisitFrequencySummaryResponse {
  date_from: string;
  date_to: string;
  holidays: string[];
  working_days: number;
  mode: VisitFrequencyMode;
  thresholds: VisitThresholds;
  totals: VisitFrequencyCounts;
  branches: VisitFrequencyBranch[];
}

export interface VisitFrequencySummaryParams {
  date_from: string;
  date_to: string;
  holidays?: string[];
  branch_code?: string;
}

/** Literal `none` asks for the shops without a route. */
export const NO_ROUTE_PARAM = "none";

export interface VisitFrequencyCustomerParams extends VisitFrequencySummaryParams {
  route_number?: string;
  bucket?: VisitBucketKey;
  search?: string;
  page?: number;
  limit?: number;
}

export interface VisitFrequencyCustomer {
  customer_uuid: string;
  customer_code: string;
  customer_name: string;
  route_number: string | null;
  route_code: string | null;
  visit_days: number;
  bill_days: number;
  pwa_visit_days: number;
  visited_not_sold_days: number;
  bucket: VisitBucketKey;
  /** Latest bill on or before `date_to`, any time back; `null` if the shop never bought. */
  last_bill_date: string | null;
}

export interface VisitFrequencyCustomerResponse {
  rows: VisitFrequencyCustomer[];
  count: number;
}
